import { Link } from 'react-router-dom';
import { PageHeader } from '@solid/ui';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Loader2, Settings2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { formsApi } from '@/lib/api';
import { useAuthStore, getIsAgencyMember } from '@/stores/auth.store';
import { FormListRow } from '@/components/forms/FormListRow';
import { formatDateShort } from '@/components/forms/form-utils';

/** /forms — everything I've been asked to fill, can fill, or have filled. */
export function Forms() {
  const { user } = useAuthStore();
  const isAgencyMember = getIsAgencyMember(user);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['myForms'],
    queryFn: () => formsApi.my(),
    staleTime: 15_000,
  });

  const assigned = data?.assigned ?? [];
  const open = data?.open ?? [];
  const submitted = data?.submitted ?? [];
  const nothing = assigned.length === 0 && open.length === 0 && submitted.length === 0;

  return (
    <div className="w-full">
      <PageHeader
        title="Forms"
        subtitle="Questionnaires and requests that need your input."
        actions={
          isAgencyMember && (
            <Link to="/forms/manage" className="btn-outline inline-flex items-center gap-2">
              <Settings2 className="w-4 h-4" /> Manage forms
            </Link>
          )
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
        </div>
      ) : isError ? (
        <Card>
          <p className="text-sm text-secondary">Couldn't load your forms.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
            Retry
          </Button>
        </Card>
      ) : nothing ? (
        <Card padding="none">
          <EmptyState
            icon={ClipboardList}
            title="Nothing to fill out"
            description="When someone asks you to complete a form, it will show up here — and you'll get an email."
            action={
              isAgencyMember ? (
                <Link to="/forms/manage" className="btn-primary">
                  Manage forms
                </Link>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <div className="space-y-6">
          <Section title="To do" count={assigned.length} empty="You're all caught up.">
            {assigned.map((r) => (
              <FormListRow
                key={r.id}
                formId={r.formId}
                title={r.form.title}
                description={r.form.description}
                myResponse={r}
                dueDate={r.dueDate}
                meta={
                  <>
                    {r.form.project?.name ? <span>{r.form.project.name}</span> : <span>Solid Digital</span>}
                    {r.assignedBy && <span>Requested by {r.assignedBy.name || r.assignedBy.email}</span>}
                  </>
                }
              />
            ))}
          </Section>

          {open.length > 0 && (
            <Section title="Open forms" count={open.length} hint="Anyone in the project can submit these, as often as needed.">
              {open.map((f) => (
                <FormListRow
                  key={f.id}
                  formId={f.id}
                  title={f.title}
                  description={f.description}
                  myResponse={f.myDraft ?? f.myLastSubmitted}
                  repeatable
                  mySubmittedCount={f.mySubmittedCount}
                  meta={<span>{f.project.name}</span>}
                />
              ))}
            </Section>
          )}

          {submitted.length > 0 && (
            <Section title="Submitted" count={submitted.length}>
              {submitted.map((r) => (
                <FormListRow
                  key={r.id}
                  formId={r.formId}
                  title={r.form.title}
                  myResponse={r}
                  meta={
                    <>
                      {r.form.project?.name ? <span>{r.form.project.name}</span> : <span>Solid Digital</span>}
                      <span>Submitted {formatDateShort(r.submittedAt)}</span>
                    </>
                  }
                />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, count, hint, empty, children }: { title: string; count: number; hint?: string; empty?: string; children: React.ReactNode }) {
  return (
    <Card padding="none">
      <div className="flex items-center justify-between px-4 py-3 border-b border-grey-100 bg-grey-50/50">
        <div>
          <span className="text-sm font-medium text-secondary">{title}</span>
          {hint && <span className="hidden sm:inline text-xs text-grey-500 ml-2">{hint}</span>}
        </div>
        <span className="text-xs text-grey-400 tabular-nums">({count})</span>
      </div>
      {count === 0 ? <p className="px-4 py-6 text-sm text-grey-500 text-center">{empty}</p> : <div className="divide-y divide-grey-100">{children}</div>}
    </Card>
  );
}
